import { Button, Drawer } from '@toggle/design-system';
import { formatTicker } from '@toggle/toggle';
import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ExpandableDrawer } from '~/components/expandable-drawer/ExpandableDrawer';
import { useNavigateToAssetOverview } from '~/hooks/use-navigate-to-asset-overview/useNavigateToAssetOverview';
import { TabsType } from '~/modules/tab-content/types';
import { useEntity } from '~/react-query-hooks/entities/useEntities';
import { POSTHOG_TRACKING_SOURCES } from '~/services/tracking/posthog/constants';
import { useFlags } from '~/stores/use-flags/useFlags';
import { useSubscription } from '~/stores/use-subscription/useSubscription';
import { DownloadButton } from '~/views/stream/components/download-button/DownloadButton';
import { handleDownloadDrawer } from '~/views/stream/utils/handleDownloadDrawer';
import { EntityFullViewDrawer } from '~/widgets/catalyst-insights-drawer/catalyst-drawer-header/company-catalyst-drawer/full-view-drawer/EntityFullViewDrawer';

import { EntityHeaderWatchlist } from './earnings-insight-widget/components/entity-header-watchlist/EntityHeaderWatchlist';
import { EarningsInsightWidget } from './earnings-insight-widget/EarningsInsightWidget';
import styles from './EarningsInsightDrawer.module.scss';
import { useEarningsInsightDrawer } from './useEarningsInsightDrawer';

export const EarningsInsightDrawer = () => {
  const { t } = useTranslation([
    'earnings',
    'common',
    'chat',
    'widget',
    'insights',
  ]);
  const { navigateToPage } = useNavigateToAssetOverview();
  const isUniversalDrawerEnabled = useFlags(state =>
    state.getFeatureFlagState('universal_drawer')
  );

  const isBasicOrLightUser = useSubscription(state =>
    state.isBasicOrLightUser()
  );

  const [expandCitations, setExpandCitations] = useState(false);

  const contentWrapperRef = useRef<HTMLDivElement>(null);

  const { isDrawerOpen, closeDrawer, activeEntityTag } =
    useEarningsInsightDrawer();

  const { data: entityData } = useEntity(activeEntityTag ?? '');
  const entity = entityData?.data;

  const handleDownload = () => {
    if (contentWrapperRef.current) {
      handleDownloadDrawer({
        contentToPrint: contentWrapperRef.current,
        entityTag: activeEntityTag,
        expandAccordions: setExpandCitations,
      });
    }
  };

  if (!activeEntityTag) {
    return null;
  }

  const headerButton = (
    <div className={styles.headerButtons}>
      <Button
        variant="empty"
        label={t('earnings:earningsInsight.drawer.viewOnAO')}
        iconName="Expand"
        size="xsmall"
        onClick={() =>
          navigateToPage({
            entityTag: activeEntityTag,
            tab: TabsType.Earnings,
          })
        }
      />
      <DownloadButton onClick={handleDownload} />
    </div>
  );

  const drawerBody = (
    <div
      className={!isUniversalDrawerEnabled ? styles.contentWrapper : undefined}
      ref={contentWrapperRef}
    >
      <EarningsInsightWidget
        expandCitations={expandCitations}
        entity={entity}
      />
    </div>
  );

  if (isUniversalDrawerEnabled && !isBasicOrLightUser) {
    return (
      <ExpandableDrawer
        className={styles.styledDrawer}
        data-testid="earnings-insight-drawer"
        isOpen={isDrawerOpen}
        handleCloseClick={closeDrawer}
        headerButton={headerButton}
        defaultWidth={740}
        label={
          entity
            ? t('insights:viewAllSection.whatElseIsMoving', {
                name: formatTicker(entity),
              })
            : activeEntityTag
        }
        subHeaderNode={
          entity && (
            <EntityHeaderWatchlist
              entity={entity}
              logoSize={48}
              className={styles.entityHeaderWatchlist}
            />
          )
        }
        fullViewContent={
          entity && (
            <EntityFullViewDrawer
              entity={entity}
              trackingSource={POSTHOG_TRACKING_SOURCES.EARNINGS_INSIGHT_DRAWER}
            />
          )
        }
      >
        {drawerBody}
      </ExpandableDrawer>
    );
  }

  return (
    <Drawer
      className={styles.legacyDrawer}
      data-testid="earnings-insight-drawer"
      isOpen={isDrawerOpen}
      handleCloseClick={closeDrawer}
      headerButton={headerButton}
    >
      {drawerBody}
    </Drawer>
  );
};
